import React, { useState, useCallback, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { 
  ArrowLeft, 
  Mic, 
  Square, 
  Play, 
  Pause, 
  Send, 
  Trash2, 
  Volume2,
  Users, 
  Lock, 
  UserCheck, 
  Shield,
  X,
  Sparkles,
  Zap
} from 'lucide-react';
import { useLanguage } from '../../hooks/useLanguage';
import { useUI } from '../../hooks/useUI';
import { useAuth } from '../../hooks/useAuth';
import { SCREENS } from '../../utils/constants';

/**
 * Voice Creation Screen - Futuristic iPhone 17 iOS 26 Design
 * Dopamine-optimized interface for recording and sharing voice notes
 */
const VoiceCreationScreen = () => {
  const { t } = useLanguage();
  const { navigateToScreen, screenParams, addFeedItem } = useUI();
  const { user } = useAuth();

  // State management
  const [isRecording, setIsRecording] = useState(false);
  const [isPlaying, setIsPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [title, setTitle] = useState('');
  const [selectedPrivacy, setSelectedPrivacy] = useState('circle');
  const [showPrivacyModal, setShowPrivacyModal] = useState(false);
  const [isPosting, setIsPosting] = useState(false);
  const [audioWaveform, setAudioWaveform] = useState([]);

  // Refs
  const mediaRecorderRef = useRef(null);
  const audioRef = useRef(null);
  const intervalRef = useRef(null);
  const streamRef = useRef(null);

  // Privacy options
  const privacyOptions = [
    {
      id: 'circle',
      title: t('privacy.circle', 'Circle'),
      description: t('privacy.circleDesc', 'Tüm circle üyelerine görünür'),
      icon: Users,
      gradient: 'from-green-500 to-emerald-600',
      emoji: '👥'
    },
    {
      id: 'close',
      title: t('privacy.closeCircle', 'Yakın Circle'),
      description: t('privacy.closeDesc', 'Sadece yakın arkadaşlarına görünür'),
      icon: UserCheck,
      gradient: 'from-amber-500 to-orange-600',
      emoji: '❤️'
    },
    {
      id: 'selected',
      title: t('privacy.selected', 'Seçili Kişiler'),
      description: t('privacy.selectedDesc', 'Belirlediğin kişilere görünür'),
      icon: Shield,
      gradient: 'from-blue-500 to-cyan-600',
      emoji: '👤'
    },
    {
      id: 'vault',
      title: t('privacy.vault', 'Dijital Kasa'),
      description: t('privacy.vaultDesc', 'Sadece sana görünür, şifrelenmiş'),
      icon: Lock,
      gradient: 'from-purple-600 to-violet-700',
      emoji: '🔒'
    }
  ];

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
        mediaRecorderRef.current.stop();
      }
      if (streamRef.current) {
        streamRef.current.getTracks().forEach(track => track.stop());
      }
    };
  }, []);

  // Generate mock waveform for visual effect
  useEffect(() => {
    if (isRecording) {
      const interval = setInterval(() => {
        setAudioWaveform(prev => {
          const newWave = [...prev, Math.random() * 100];
          return newWave.slice(-50); // Keep last 50 samples
        });
      }, 100);
      return () => clearInterval(interval);
    }
  }, [isRecording]);

  // Haptic feedback
  const triggerHaptic = useCallback((type = 'soft') => {
    if (window.navigator && window.navigator.vibrate) {
      const patterns = {
        soft: [10],
        medium: [20],
        strong: [30]
      };
      window.navigator.vibrate(patterns[type] || patterns.soft);
    }
  }, []);

  // Format duration
  const formatDuration = useCallback((seconds) => {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    return `${mins}:${secs.toString().padStart(2, '0')}`;
  }, []);

  // Start recording
  const startRecording = useCallback(async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      streamRef.current = stream;
      
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      
      const chunks = [];
      mediaRecorder.ondataavailable = (event) => {
        chunks.push(event.data);
      };
      
      mediaRecorder.onstop = () => {
        const blob = new Blob(chunks, { type: 'audio/wav' });
        setRecordedBlob(blob);
        stream.getTracks().forEach(track => track.stop());
      };
      
      mediaRecorder.start();
      setIsRecording(true);
      setDuration(0);
      setAudioWaveform([]);
      triggerHaptic('medium');
      
      // Start duration timer
      intervalRef.current = setInterval(() => {
        setDuration(prev => prev + 1);
      }, 1000);
      
    } catch (error) {
      console.error('Error starting recording:', error);
    }
  }, [triggerHaptic]);

  // Stop recording
  const stopRecording = useCallback(() => {
    if (mediaRecorderRef.current && mediaRecorderRef.current.state !== 'inactive') {
      mediaRecorderRef.current.stop();
    }
    setIsRecording(false);
    triggerHaptic('soft');
    
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }
  }, [triggerHaptic]);

  // Play/pause recording
  const togglePlayback = useCallback(() => {
    if (!recordedBlob) return;
    
    if (isPlaying) {
      audioRef.current?.pause();
      setIsPlaying(false);
    } else {
      if (audioRef.current) {
        audioRef.current.play();
        setIsPlaying(true);
      } else {
        const audio = new Audio(URL.createObjectURL(recordedBlob));
        audioRef.current = audio;
        audio.onended = () => setIsPlaying(false);
        audio.play();
        setIsPlaying(true);
      }
    }
    triggerHaptic('soft');
  }, [recordedBlob, isPlaying, triggerHaptic]);

  // Delete recording
  const deleteRecording = useCallback(() => {
    setRecordedBlob(null);
    setDuration(0);
    setAudioWaveform([]);
    setIsPlaying(false);
    if (audioRef.current) {
      audioRef.current.pause();
      audioRef.current = null;
    }
    triggerHaptic('medium');
  }, [triggerHaptic]);

  // Handle back navigation
  const handleBack = useCallback(() => {
    triggerHaptic('soft');
    navigateToScreen(SCREENS.IDENTITY_CIRCLE);
  }, [navigateToScreen, triggerHaptic]);

  // Handle privacy selection
  const handlePrivacySelect = useCallback((privacy) => {
    setSelectedPrivacy(privacy);
    setShowPrivacyModal(false);
    triggerHaptic('soft');
  }, [triggerHaptic]);  // Handle post
  const handlePost = useCallback(async () => {
    if (!recordedBlob || isPosting) return;

    setIsPosting(true);
    triggerHaptic('medium');

    try {
      // Simulate API call
      await new Promise(resolve => setTimeout(resolve, 2000));
        // Create new feed item
      const newPost = {
        type: 'voice',
        user: {
          name: user?.name || 'You',
          profilePhoto: user?.profilePhoto || 'https://images.unsplash.com/photo-1535713875002-d1d0cf377fde?w=150&h=150&fit=crop&crop=face&auto=format&q=80',
          avatar: user?.name?.charAt(0) || 'Y',
          verified: user?.verified || false
        },
        content: title.trim() || 'Voice note',
        audioUrl: recordedBlob ? URL.createObjectURL(recordedBlob) : null,
        duration: formatDuration(duration),
        timestamp: new Date(),
        privacy: selectedPrivacy,
        likes: 0,
        comments: [],
        shares: 0,
        tags: ['voice']
      };
      
      // Add to feed
      addFeedItem(newPost);
      
      console.log('Posting voice note:', {
        title: title.trim(),
        duration,
        privacy: selectedPrivacy,
        timestamp: Date.now()
      });

      // Navigate back with success
      navigateToScreen(SCREENS.IDENTITY_CIRCLE);
    } catch (error) {
      console.error('Failed to post voice note:', error);
    } finally {
      setIsPosting(false);
    }
  }, [recordedBlob, title, duration, selectedPrivacy, isPosting, navigateToScreen, triggerHaptic, addFeedItem, user]);  // Get selected privacy option
  const selectedPrivacyOption = privacyOptions.find(opt => opt.id === selectedPrivacy);
  const SelectedPrivacyIcon = selectedPrivacyOption?.icon;
  // Check if can post - only require recorded audio
  const canPost = recordedBlob && !isPosting;

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 relative overflow-hidden">
      {/* Animated background */}
      <div className="absolute inset-0 overflow-hidden">
        <motion.div
          className="absolute inset-0 opacity-20"
          animate={{
            background: [
              'radial-gradient(circle at 20% 50%, rgba(34, 197, 94, 0.3) 0%, transparent 50%)',
              'radial-gradient(circle at 80% 20%, rgba(16, 185, 129, 0.3) 0%, transparent 50%)',
              'radial-gradient(circle at 40% 80%, rgba(6, 182, 212, 0.3) 0%, transparent 50%)'
            ]
          }}
          transition={{ duration: 8, repeat: Infinity, repeatType: 'reverse' }}
        />
        
        {/* Sound waves animation */}
        {isRecording && (
          <div className="absolute inset-0 flex items-center justify-center">
            {[...Array(8)].map((_, i) => (
              <motion.div
                key={i}
                className="w-1 bg-emerald-400/30 rounded-full mx-1"
                animate={{
                  height: [20, 60, 20],
                  opacity: [0.3, 0.8, 0.3]
                }}
                transition={{
                  duration: 1,
                  repeat: Infinity,
                  delay: i * 0.1,
                  ease: "easeInOut"
                }}
              />
            ))}
          </div>
        )}
      </div>

      {/* Header */}
      <motion.div 
        className="relative z-10 flex items-center justify-between p-6 backdrop-blur-xl bg-slate-800/40 border-b border-white/10"
        initial={{ opacity: 0, y: -20 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.5 }}
      >
        <div className="flex items-center space-x-4">
          <motion.button
            className="w-10 h-10 bg-white/10 backdrop-blur-xl rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
            whileHover={{ scale: 1.1 }}
            whileTap={{ scale: 0.9 }}
            onClick={handleBack}
          >
            <ArrowLeft className="w-5 h-5" />
          </motion.button>
          
          <div className="flex items-center space-x-3">
            <div className="w-10 h-10 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center">
              <Mic className="w-5 h-5 text-white" />
            </div>
            <div>
              <h1 className="text-xl font-bold text-white">
                {t('create.voiceNote', 'Sesli Not')}
              </h1>
              <p className="text-sm text-white/60">
                {t('create.recordYourThoughts', 'Düşüncelerini kaydet')}
              </p>
            </div>
          </div>
        </div>

        <motion.button
          className={`px-6 py-3 rounded-2xl font-semibold transition-all flex items-center space-x-2 ${
            canPost
              ? 'bg-gradient-to-r from-emerald-500 to-teal-600 text-white shadow-lg hover:shadow-emerald-500/25'
              : 'bg-white/10 text-white/50 cursor-not-allowed'
          }`}
          whileHover={canPost ? { scale: 1.05, y: -2 } : {}}
          whileTap={canPost ? { scale: 0.95 } : {}}
          onClick={handlePost}
          disabled={!canPost}
        >
          {isPosting ? (
            <>
              <motion.div
                className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              />
              <span>{t('common.posting', 'Paylaşılıyor...')}</span>
            </>
          ) : (
            <>
              <Send className="w-4 h-4" />
              <span>{t('common.share', 'Paylaş')}</span>
            </>
          )}
        </motion.button>
      </motion.div>

      {/* Content Area */}
      <div className="relative z-10 p-6 space-y-6">
        {/* Recording Control Center */}
        <motion.div
          className="bg-gradient-to-br from-slate-800/60 via-slate-700/40 to-slate-900/60 backdrop-blur-xl rounded-3xl p-8 border border-white/10 shadow-xl"
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.5, delay: 0.1 }}
        >
          {/* Central Recording Button */}
          <div className="flex flex-col items-center space-y-6">
            <motion.button
              className={`w-32 h-32 rounded-full flex items-center justify-center transition-all duration-300 ${
                isRecording 
                  ? 'bg-gradient-to-br from-red-500 to-red-600 shadow-2xl shadow-red-500/40' 
                  : 'bg-gradient-to-br from-emerald-500 to-teal-600 shadow-2xl shadow-emerald-500/40'
              }`}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              onClick={isRecording ? stopRecording : startRecording}
              disabled={isPlaying}
            >
              {isRecording ? (
                <Square className="w-12 h-12 text-white" />
              ) : (
                <Mic className="w-12 h-12 text-white" />
              )}
              
              {/* Pulsing ring animation when recording */}
              {isRecording && (
                <motion.div
                  className="absolute inset-0 rounded-full border-4 border-white/30"
                  animate={{ scale: [1, 1.3, 1], opacity: [0.7, 0, 0.7] }}
                  transition={{ duration: 2, repeat: Infinity }}
                />
              )}
            </motion.button>

            {/* Status and Duration */}
            <div className="text-center">
              <motion.p 
                className="text-2xl font-bold text-white mb-2"
                animate={isRecording ? { scale: [1, 1.1, 1] } : {}}
                transition={{ duration: 1, repeat: Infinity }}
              >
                {formatDuration(duration)}
              </motion.p>
              <p className="text-white/60">
                {isRecording 
                  ? t('voice.recording', 'Kaydediliyor...') 
                  : recordedBlob 
                    ? t('voice.recorded', 'Kayıt tamamlandı') 
                    : t('voice.tapToRecord', 'Kaydetmek için dokun')
                }
              </p>
            </div>

            {/* Waveform Visualization */}
            {(isRecording || recordedBlob) && (
              <motion.div 
                className="flex items-center justify-center space-x-1 h-16"
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
              >
                {audioWaveform.map((height, index) => (
                  <motion.div
                    key={index}
                    className="w-1 bg-gradient-to-t from-emerald-400 to-teal-400 rounded-full"
                    style={{ height: Math.max(4, height / 2) }}
                    animate={{ opacity: [0.4, 1, 0.4] }}
                    transition={{ duration: 1, repeat: Infinity, delay: index * 0.1 }}
                  />
                ))}
              </motion.div>
            )}
          </div>

          {/* Playback Controls */}
          {recordedBlob && (
            <motion.div 
              className="flex items-center justify-center space-x-4 mt-6 pt-6 border-t border-white/10"
              initial={{ opacity: 0, y: 20 }}
              animate={{ opacity: 1, y: 0 }}
            >
              <motion.button
                className="w-12 h-12 bg-white/10 backdrop-blur-xl rounded-full flex items-center justify-center text-white hover:bg-white/20 transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={togglePlayback}
              >
                {isPlaying ? <Pause className="w-5 h-5" /> : <Play className="w-5 h-5" />}
              </motion.button>
              
              <motion.button
                className="w-12 h-12 bg-red-500/20 backdrop-blur-xl rounded-full flex items-center justify-center text-red-400 hover:bg-red-500/30 transition-colors"
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
                onClick={deleteRecording}
              >
                <Trash2 className="w-5 h-5" />
              </motion.button>
            </motion.div>
          )}
        </motion.div>

        {/* Title input */}
        {recordedBlob && (
          <motion.div
            className="bg-gradient-to-br from-slate-800/60 via-slate-700/40 to-slate-900/60 backdrop-blur-xl rounded-2xl p-4 border border-white/10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.2 }}
          >
            <div className="flex items-center space-x-3">
              <Sparkles className="w-5 h-5 text-emerald-400" />
              <input
                type="text"
                value={title}
                onChange={(e) => setTitle(e.target.value)}
                placeholder={t('voice.titlePlaceholder', 'Sesli notuna bir başlık ver... (opsiyonel)')}
                className="flex-1 bg-transparent text-white placeholder-white/40 focus:outline-none"
                maxLength={100}
              />
            </div>
          </motion.div>
        )}

        {/* Privacy selection */}
        {recordedBlob && (
          <motion.div
            className="bg-gradient-to-br from-slate-800/60 via-slate-700/40 to-slate-900/60 backdrop-blur-xl rounded-2xl border border-white/10"
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5, delay: 0.3 }}
          >
            <button
              className="w-full p-4 flex items-center justify-between hover:bg-white/5 transition-colors rounded-2xl"
              onClick={() => setShowPrivacyModal(true)}
            >
              <div className="flex items-center space-x-3">
                <div className={`w-10 h-10 bg-gradient-to-r ${selectedPrivacyOption?.gradient} rounded-xl flex items-center justify-center`}>
                  <SelectedPrivacyIcon className="w-5 h-5 text-white" />
                </div>
                <div className="text-left">
                  <h3 className="text-white font-semibold">{selectedPrivacyOption?.title}</h3>
                  <p className="text-white/60 text-sm">{selectedPrivacyOption?.description}</p>
                </div>
              </div>
              <motion.div
                whileHover={{ scale: 1.1 }}
                className="w-8 h-8 bg-white/10 rounded-full flex items-center justify-center"
              >
                <span className="text-white/60">›</span>
              </motion.div>
            </button>
          </motion.div>
        )}
      </div>

      {/* Privacy Selection Modal */}
      <AnimatePresence>
        {showPrivacyModal && (
          <motion.div
            className="fixed inset-0 z-50 flex items-center justify-center p-4"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={() => setShowPrivacyModal(false)}
          >
            {/* Backdrop */}
            <motion.div
              className="absolute inset-0 bg-black/70 backdrop-blur-2xl"
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
            />
            
            {/* Modal content */}
            <motion.div
              className="relative w-full max-w-lg bg-gradient-to-br from-slate-800/95 via-slate-700/90 to-slate-900/95 backdrop-blur-3xl rounded-3xl p-8 border border-white/10 shadow-2xl"
              initial={{ scale: 0.8, opacity: 0, y: 50 }}
              animate={{ scale: 1, opacity: 1, y: 0 }}
              exit={{ scale: 0.8, opacity: 0, y: 50 }}
              onClick={(e) => e.stopPropagation()}
            >
              <div className="text-center mb-8">
                <motion.div
                  className="w-16 h-16 bg-gradient-to-br from-emerald-500 to-teal-600 rounded-full flex items-center justify-center mx-auto mb-4"
                  animate={{ rotate: [0, 360] }}
                  transition={{ duration: 3, repeat: Infinity, ease: "linear" }}
                >
                  <Volume2 className="w-8 h-8 text-white" />
                </motion.div>
                <h2 className="text-2xl font-bold text-white mb-3">
                  {t('privacy.title', 'Gizlilik Ayarları')}
                </h2>
                <p className="text-white/70">
                  {t('privacy.subtitle', 'Sesli notunu kimler duyabilsin?')}
                </p>
              </div>

              <div className="space-y-4">
                {privacyOptions.map((option) => {
                  const IconComponent = option.icon;
                  const isSelected = selectedPrivacy === option.id;
                  return (
                    <motion.button
                      key={option.id}
                      className={`w-full p-5 rounded-2xl bg-gradient-to-r ${option.gradient} flex items-center space-x-4 text-white shadow-xl border-2 transition-all ${
                        isSelected ? 'border-white/40 scale-105' : 'border-white/10'
                      }`}
                      whileHover={{ scale: isSelected ? 1.05 : 1.02, y: -2 }}
                      whileTap={{ scale: 0.98 }}
                      onClick={() => handlePrivacySelect(option.id)}
                    >
                      <div className="w-12 h-12 bg-white/25 rounded-xl flex items-center justify-center">
                        <IconComponent className="w-6 h-6" />
                      </div>
                      <div className="flex-1 text-left">
                        <h3 className="font-bold text-lg">{option.title}</h3>
                        <p className="text-sm text-white/90 mt-1">{option.description}</p>
                      </div>
                      <motion.div
                        className={`w-6 h-6 border-2 border-white/50 rounded-full flex items-center justify-center ${
                          isSelected ? 'bg-white/20' : ''
                        }`}
                        whileHover={{ scale: 1.2 }}
                      >
                        {isSelected && (
                          <motion.div
                            className="w-3 h-3 bg-white rounded-full"
                            initial={{ scale: 0 }}
                            animate={{ scale: 1 }}
                            transition={{ type: "spring", stiffness: 300 }}
                          />
                        )}
                      </motion.div>
                    </motion.button>
                  );
                })}
              </div>

              {/* Close button */}
              <motion.button
                className="absolute top-4 right-4 w-10 h-10 bg-white/20 backdrop-blur-xl rounded-full flex items-center justify-center text-white hover:bg-white/30 transition-colors"
                onClick={() => setShowPrivacyModal(false)}
                whileHover={{ scale: 1.1 }}
                whileTap={{ scale: 0.9 }}
              >
                <X className="w-5 h-5" />
              </motion.button>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default VoiceCreationScreen;
